import {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMutation} from '@tanstack/react-query';
import {Alert, Button, Form, Input} from 'antd';
import clsx from 'clsx';

import {login, type LoginRequest, LoginRequestSchema} from '@services/auth';
import {env, HttpError} from '@services/utils';
import {useAuth} from '@hooks/useAuth';
import {useBreakpoint} from '@hooks/useBreakpoint';
import {BrandMark} from '@components/BrandMark';
import {PermissionPickerModal} from '@components/PermissionPickerModal';
import {brand} from '@utils/brand';

import {mapAuthError} from './Login.utils';

import styles from './Login.module.scss';

export const Login = () => {
  const auth = useAuth();
  const {isMobile, isTablet} = useBreakpoint();

  const [pickerOpen, setPickerOpen] = useState(false);

  const {control, handleSubmit, formState} = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: {
      email: env.VITE_DEV_EMAIL || '',
      password: env.VITE_DEV_PASSWORD || '',
    },
    mode: 'onTouched',
  });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: ({token, subToken, user}) => {
      // BE отдаёт два токена: `subToken` — для всех запросов, `token` —
      // fallback. Authorization: Bearer ${subToken || token}.
      auth.setSession(subToken ?? token, user);
      setPickerOpen(true);
    },
  });

  const handleFormSubmit = handleSubmit((values) => {
    mutation.mutate(values);
  });

  const errorMessage =
    mutation.error instanceof HttpError
      ? mapAuthError(mutation.error)
      : (mutation.error?.message ?? null);

  const form = (
    <div className={styles.formCard}>
      <h2 className={styles.formTitle}>Вход в аккаунт</h2>
      <p className={styles.formSubtitle}>Введите email и пароль кассира</p>

      {errorMessage && (
        <Alert className={styles.alert} type="error" showIcon message={errorMessage} />
      )}

      <Form layout="vertical" onFinish={handleFormSubmit}>
        <Controller
          control={control}
          name="email"
          render={({field, fieldState}) => (
            <Form.Item
              label="Email"
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <Input
                {...field}
                type="email"
                autoComplete="email"
                placeholder={brand.emailPlaceholder}
                size="large"
                autoFocus
              />
            </Form.Item>
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({field, fieldState}) => (
            <Form.Item
              label="Пароль"
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <Input.Password
                {...field}
                autoComplete="current-password"
                placeholder="••••••••"
                size="large"
              />
            </Form.Item>
          )}
        />

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={mutation.isPending}
          disabled={!formState.isValid && formState.isSubmitted}
          className={styles.submit}
        >
          Войти
        </Button>
      </Form>
    </div>
  );

  const picker = <PermissionPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />;

  if (isTablet) {
    return (
      <div className={styles.rootMobile}>
        <div className={clsx(styles.brandMobile, isMobile && styles.brandMobileCompact)}>
          <BrandMark size={32} showWordmark />
          <h1 className={styles.heroMobile}>Управляйте событиями и продажами с телефона.</h1>
        </div>
        <div className={clsx(styles.formHostMobile, isMobile && styles.formHostMobileCompact)}>
          {form}
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div className={styles.rootDesktop}>
      <aside className={styles.brandDesktop}>
        <BrandMark size={36} showWordmark />
        <div>
          <h1 className={styles.heroDesktop}>
            Управляйте событиями, продажами и финансами в одном окне.
          </h1>
        </div>
        <span className={styles.copyright}>© {new Date().getFullYear()} {brand.name}</span>
      </aside>

      <section className={styles.formHost}>
        <div className={styles.formInner}>{form}</div>
      </section>

      {picker}
    </div>
  );
};
